import React, { useEffect } from "react"
import ReactDOM from "react-dom"
import moment from "moment"

import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"
import useFetchResource from "~/hooks/useFetchResource"
import getAppConfig from "~/utils/getAppConfig"
import { apiStatisticsKitchenProduction } from "~/routes"

interface KitchenItem {
  name: string
  quantity: number
}

interface KitchenProductionReportProps {
  startDate: string
  endDate: string
  isPrinting: boolean
  onAfterPrint: () => void
  onItemsChange: (hasItems: boolean) => void
}

const KitchenProductionReport = ({
  startDate,
  endDate,
  isPrinting,
  onAfterPrint,
  onItemsChange,
}: KitchenProductionReportProps) => {
  const { venueSlug } = getAppConfig()

  const { data: items, isLoading, isRefetching, refetch } = useFetchResource<
    { items: KitchenItem[] },
    "items"
  >(
    apiStatisticsKitchenProduction.index.path({ venueSlug }),
    "items",
    { start_date: startDate, end_date: endDate }
  )

  const safeItems: KitchenItem[] = Array.isArray(items) ? items : []

  useEffect(() => {
    refetch({ start_date: startDate, end_date: endDate })
  }, [startDate, endDate])

  useEffect(() => {
    if (!isLoading && !isRefetching) {
      onItemsChange(safeItems.length > 0)
    }
  }, [safeItems.length, isLoading, isRefetching])

  useEffect(() => {
    if (!isPrinting) return

    const handleAfterPrint = () => onAfterPrint()
    window.addEventListener("afterprint", handleAfterPrint)
    window.print()

    return () => window.removeEventListener("afterprint", handleAfterPrint)
  }, [isPrinting])

  const dateLabel =
    startDate === endDate
      ? moment(startDate).format("MMMM D, YYYY")
      : `${moment(startDate).format("MMM D")} – ${moment(endDate).format("MMM D, YYYY")}`

  return (
    <>
      <LoadingWrapper isLoading={isLoading || isRefetching}>
        <CatchCard className="card-dark-theme">
          <CatchTable
            data={safeItems}
            emptyMessage={`No items ordered for ${dateLabel}`}
            columns={[
              { key: "name", header: "Item" },
              {
                key: "quantity",
                header: "Quantity",
                width: "160px",
                formatter: (qty) => <strong>{qty}</strong>,
              },
            ]}
          />
        </CatchCard>
      </LoadingWrapper>

      {isPrinting &&
        ReactDOM.createPortal(
          <div id="kitchen-production-print-portal">
            <div id="kitchen-production-print">
              <div className="kp-header">
                <h1>Kitchen Production Report</h1>
                <div className="kp-date">{dateLabel}</div>
              </div>
              <table className="kp-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {safeItems.map((item) => (
                    <tr key={item.name}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="kp-footer">
                Printed on {moment().format("MMMM D, YYYY [at] h:mm a")}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default KitchenProductionReport
